import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import {
  onboardingAgentLoginRoute,
  onboardingAgentOtpRoute,
  onboardingBrokerRoute,
  onboardingContinueOtpRoute,
  onboardingContinueReferenceRoute,
  onboardingCreateAccountRoute,
  onboardingLetsGetStartedRoute,
  onboardingPostDistributionOtp,
  onboardingRoutePos,
  onboardingRoutePosLogin,
} from 'src/routing/routes';
import { PageNotFound } from 'src/containers';
import { FeesProvider } from 'src/context/fees-context';
import { SalesforceProvider } from 'src/context/salesforce-context';
import { PaymentGatewayProvider } from 'src/context/payment-gateway-context';
import LetsGetStarted from './lets-get-started/lets-get-started';
import AgentLogin from './agent-login/agent-login';
import CreateAccount from './create-account/create-account';
import EnterReference from './continue-your-application/enter-reference/enter-reference';
import EnterOtp from './continue-your-application/otp/enter-otp';
import { SalesforceService } from 'src/services/salesforce.service';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import TechnicalError from './technical-error/technical-error';
import {
  AssistantIcon,
  Basket,
  BlueTick,
  Briefcase,
  buyInstore,
  buyOnline,
  Camera,
  CloseCorp,
  Company,
  Document,
  HelpImage,
  Id,
  InfoImage,
  NPO,
  Partnership,
  SelectedItem,
  SoleProp,
  Trust,
  VodapayLogoV2,
  VodapayLogoWithTextV2,
} from 'src/assets/images';
import { ImageCache } from 'src/components/image-cache/image-cache';
import AgentOtp from './agent-login/agent-otp/agent-otp';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Channel, CHANNEL_KEY } from './helpers/channel';
import { QueryConfigContext } from 'src/context/query-config-context';
import PosDistributionOtp from './pos-distribution/otp/pos-distribution-otp';
import PosDistributionStart from './pos-distribution/start/pos-distribution-start';
import { ReducerContextProvider } from 'src/context/reducer-context';
import { DisplaySizeContextProvider } from 'src/context/display-size-context';
import { Application, CacheContext } from '../../features/common';
import { PaymentStatus } from '../../features/common/types/application/payment';
import { Environment } from 'src/Environment';
import './onboarding.scss';

const SalesforceProviderWithService = ({ children }) => {
  const handleTechnicalError = useTechnicalErrorHandler();
  return (
    <SalesforceProvider salesforceService={SalesforceService(handleTechnicalError)}>
      {children}
    </SalesforceProvider>
  );
};

export default function Onboarding() {
  const location = useLocation();
  const { queryConfig } = useContext(QueryConfigContext);
  const isVodacomBusinessChannel = queryConfig[CHANNEL_KEY] === Channel.VODACOM_BUSINESS;

  // TODO: REMOVE TECH DEBT: this is a hack to make the old payment gateway URL work with the pos revamp journey.
  // TODO: Remove when we get our redirect URL added to the payment gateway
  const APPLICATION_CACHE_KEY = 'application';
  const cacheStore = useContext(CacheContext);
  const [redirectToOnboardingLight, setRedirectToOnboardingLight] = useState(false);

  const redirectLegacyUrls = useMemo(
    () => !Environment.current.disabledFeatures.redirectLegacyUrls,
    [],
  );

  useEffect(() => {
    if (MiniAppEvents.isInMiniApp()) {
      const siteTitle = document.getElementById('siteTitle');
      if (siteTitle) {
        siteTitle.innerText = 'Get Card Machine';
      }
    }

    // TODO: REMOVE TECH DEBT: this is a hack to make the old payment gateway URL work with the pos revamp journey.
    // TODO: Remove when we get our redirect URL added to the payment gateway
    const application: Application = cacheStore.get(APPLICATION_CACHE_KEY);
    setRedirectToOnboardingLight(
      application?.payment?.paymentStatus === PaymentStatus.PAYMENT_IN_PROGRESS,
    );
  }, []);

  return (
    <div id="OnboardingContainer" className="Onboarding is-fullheight reskinned-onboarding">
      <ImageCache
        images={[
          VodapayLogoV2,
          BlueTick,
          SelectedItem,
          buyInstore,
          buyOnline,
          SoleProp,
          Partnership,
          Company,
          CloseCorp,
          NPO,
          Trust,
          Id,
          Document,
          Camera,
          HelpImage,
          Basket,
          InfoImage,
          AssistantIcon,
          Briefcase,
          VodapayLogoV2,
          VodapayLogoWithTextV2,
        ]}
      />

      {redirectLegacyUrls && (
        <>
          {window.location.pathname === onboardingAgentLoginRoute && (
            <Redirect exact from={onboardingAgentLoginRoute} to={onboardingBrokerRoute} />
          )}
          {window.location.pathname === onboardingLetsGetStartedRoute && (
            <Redirect
              from={onboardingLetsGetStartedRoute}
              to={`${onboardingRoutePos}/choose-solution${location.search}`}
            />
          )}
          {isVodacomBusinessChannel && <Redirect to={onboardingRoutePosLogin} />}
          {window.location.pathname === onboardingContinueReferenceRoute && (
            <Redirect exact from={onboardingContinueReferenceRoute} to={onboardingRoutePosLogin} />
          )}
        </>
      )}

      {
        // TODO: REMOVE TECH DEBT: this is a hack to make the old payment gateway URL work with the pos revamp journey.
        // TODO: Remove when we get our redirect URL added to the payment gateway
      }
      {redirectToOnboardingLight ? (
        <Redirect
          from="/onboarding/create-account/confirm"
          to={'/onboarding/pos' + window.location.search}
        />
      ) : (
        <ReducerContextProvider>
          <TechnicalError>
            <DisplaySizeContextProvider>
              <SalesforceProviderWithService>
                <FeesProvider>
                  <PaymentGatewayProvider>
                    <Switch>
                      <Route exact path={onboardingAgentLoginRoute} component={AgentLogin} />
                      <Route exact path={onboardingAgentOtpRoute} component={AgentOtp} />
                      <Route
                        exact
                        path={onboardingLetsGetStartedRoute}
                        component={() => {
                          return isVodacomBusinessChannel ? (
                            <PosDistributionStart />
                          ) : (
                            <LetsGetStarted />
                          );
                        }}
                      />

                      {isVodacomBusinessChannel && (
                        <Route
                          exact
                          path={onboardingPostDistributionOtp}
                          component={PosDistributionOtp}
                        />
                      )}
                      <Route path={onboardingCreateAccountRoute} component={CreateAccount} />
                      <Route path={onboardingContinueReferenceRoute} component={EnterReference} />
                      <Route path={onboardingContinueOtpRoute} component={EnterOtp} />
                      <Route component={PageNotFound} />
                    </Switch>

                    <Switch>
                      <Route path={onboardingLetsGetStartedRoute} component={''} />
                    </Switch>
                  </PaymentGatewayProvider>
                </FeesProvider>
              </SalesforceProviderWithService>
            </DisplaySizeContextProvider>
          </TechnicalError>
        </ReducerContextProvider>
      )}
    </div>
  );
}
